import { Components } from '@mui/material/styles';

import { hexToRgba } from '~/common/util/format';

import { pixelsToRem } from '../typography';
import palette from './palette';
import typography from './typography';

const createButtonGroupStyles = ({
  rootColor,
  outlined,
  contained,
}: {
  rootColor: string;
  outlined: {
    borderStyles: Record<string, string>;
    color?: string;
    hoverColor?: string;
    activeColor?: string;
    hoverOverrides?: Record<string, string>;
    activeOverrides?: Record<string, string>;
    svgColor?: string;
    touchRippleBackground?: string;
    touchRippleColor?: string;
    disabledStyles: Record<string, unknown>;
  };
  contained: {
    borderStyles?: Record<string, string>;
    backgroundColor: string;
    color?: string;
    hoverBackground: string;
    hoverBoxShadow?: string;
    activeBackground: string;
    touchRippleBackground: string;
    touchRippleColor?: string;
    disabledStyles: Record<string, unknown>;
  };
}) => ({
  color: rootColor,
  '&.MuiButton-outlined, &.MuiLink-root': {
    ...outlined.borderStyles,
    ...(outlined.color && { color: outlined.color }),
    backgroundColor: palette.white,
    '&:hover': {
      ...outlined.hoverOverrides,
      ...(outlined.hoverColor && { color: outlined.hoverColor }),
      backgroundColor: palette.blue[50],
    },
    '&:active': {
      ...outlined.activeOverrides,
      ...(outlined.activeColor && { color: outlined.activeColor }),
      backgroundColor: palette.blue[100],
    },
    ...(outlined.svgColor && { '& > svg': { color: outlined.svgColor } }),
    ...(outlined.touchRippleBackground && {
      '& .MuiTouchRipple-child': {
        backgroundColor: outlined.touchRippleBackground,
        ...(outlined.touchRippleColor && { color: outlined.touchRippleColor }),
      },
    }),
    '&.Mui-disabled': outlined.disabledStyles,
  },
  '&.MuiButton-contained': {
    ...contained.borderStyles,
    backgroundColor: contained.backgroundColor,
    ...(contained.color && { color: contained.color }),
    '&:hover': {
      backgroundColor: contained.hoverBackground,
      ...(contained.hoverBoxShadow && { boxShadow: contained.hoverBoxShadow }),
    },
    '&:active': {
      backgroundColor: contained.activeBackground,
    },
    '& .MuiTouchRipple-child': {
      backgroundColor: contained.touchRippleBackground,
      ...(contained.touchRippleColor && { color: contained.touchRippleColor }),
    },
    '&.Mui-disabled': contained.disabledStyles,
  },
});

const components = {
  MuiCssBaseline: {
    styleOverrides: {
      'html, body, #root': {
        height: '100%',
        backgroundColor: palette.bgGray[200],
        color: palette.gray[900],
        fontFamily:
          '"Roboto", Helvetica, -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
      },
      a: {
        color: palette.blue[600],
      },
      h1: typography.h1,
      h2: typography.h2,
      h3: typography.h3,
      h4: typography.h4,
      h5: typography.h5,
      h6: typography.h6,
      legend: {
        fontSize: typography.h6.fontSize,
        fontWeight: typography.h6.fontWeight,
        paddingBottom: '0.5rem',
      },
      '*::-webkit-scrollbar, *::-webkit-scrollbar-track, *::-webkit-scrollbar-corner':
        {
          backgroundColor: palette.white,
        },
      '*::-webkit-scrollbar': {
        width: '14px',
      },
      '*::-webkit-scrollbar-thumb': {
        backgroundColor: '#babac0',
        borderRadius: '16px',
        border: `4px solid ${palette.white}`,
      },
      '*::-webkit-scrollbar-button': {
        display: 'none',
      },
    },
  },
  MuiTypography: {
    styleOverrides: {
      root: {
        '.MuiInputAdornment-root &': {
          color: palette.gray[600],
        },
        '&[component="strong"], &strong, strong&': {
          fontWeight: 500,
          fontSize: 'inherit',
        },
      },
    },
  },
  MuiFormLabel: {
    styleOverrides: {
      root: {
        color: palette.gray[900],
        marginBottom: '0.5rem',
        fontWeight: `500`,
        fontSize: '1rem',
        '& > .MuiTypography-root': {
          fontWeight: `500`,
          fontSize: '1rem',
          display: 'inline',
        },
        '&.Mui-focused': {
          color: palette.gray[900],
        },
        '&.Mui-error': {
          color: palette.gray[900],
        },
        '& > legend': {
          paddingBottom: 0,
        },
        '&.Mui-disabled': {
          color: palette.gray[600],
        },
      },
      asterisk: {
        color: palette.error?.main,
      },
    },
  },
  MuiFormControl: {
    styleOverrides: {
      root: {
        display: 'block',
      },
    },
  },
  MuiFormControlLabel: {
    styleOverrides: {
      root: {
        marginLeft: 0,
      },
      '&.Mui-disabled': {
        color: palette.red[700],
      },
      label: {
        // This targets the label text
        '&.Mui-disabled': {
          color: palette.gray[600],
        },
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        height: '40px',
        borderRadius: '0.5rem',
        textTransform: 'capitalize',
        fontSize: '1rem',
        padding: '0.5rem 1rem',
        '&.MuiButton-containedPrimary': {
          backgroundColor: palette.blue[600],
          color: palette.white,
          boxShadow: 'unset',
          '&:hover': {
            backgroundColor: palette.blue[700],
          },
          '&:active': {
            backgroundColor: palette.blue[800],
          },
          '& .MuiTouchRipple-child': {
            backgroundColor: hexToRgba(palette.white, 0.4),
          },
          '&.Mui-disabled': {
            border: 'none',
            color: palette.gray[600],
            backgroundColor: palette.gray[200],
            '& .MuiButton-icon': {
              opacity: 0.4,
            },
          },
        },
        '&.MuiButton-outlinedPrimary': {
          border: `2px solid ${palette.blue[600]}`,
          color: palette.blue[600],
          backgroundColor: palette.white,
          '&:hover': {
            border: `2px solid ${palette.blue[700]}`,
            color: palette.blue[700],
            backgroundColor: palette.blue[50],
          },
          '&:active': {
            border: `2px solid ${palette.blue[800]}`,
            color: palette.blue[800],
            backgroundColor: palette.blue[100],
          },
          '& .MuiTouchRipple-child': {
            backgroundColor: palette.blue[500],
            color: palette.blue[700],
          },
          '&.Mui-disabled': {
            border: `2px solid ${palette.gray[600]}`,
            backgroundColor: palette.gray[50],
            color: palette.gray[600],
            '& .MuiButton-icon': {
              opacity: 0.4,
            },
          },
        },
        '&.MuiButton-textPrimary': {
          color: palette.blue[600],
          '&:hover': {
            color: palette.blue[700],
            backgroundColor: hexToRgba(palette.gray[200], 0.4),
          },
          '&:active': {
            color: palette.blue[700],
            backgroundColor: palette.blue[100],
          },
          '& .MuiTouchRipple-child': {
            color: palette.blue[700],
            backgroundColor: palette.blue[500],
          },
          '& .MuiButton-startIcon': {
            marginLeft: 'unset',
          },
          '&.Mui-disabled': {
            borderColor: palette.gray[500],
            backgroundColor: hexToRgba(palette.white, 0.4),
            color: palette.gray[500],
            '& .MuiButton-icon': {
              opacity: 0.4,
            },
          },
        },
        '&.MuiButtonGroup-grouped': {
          ...createButtonGroupStyles({
            rootColor: palette.gray[900],
            outlined: {
              borderStyles: { borderColor: palette.blue[300] },
              svgColor: palette.blue[600],
              touchRippleBackground: palette.blue[500],
              touchRippleColor: palette.blue[700],
              disabledStyles: {
                color: palette.gray[800],
                backgroundColor: palette.gray[50],
                borderColor: palette.gray[500],
                opacity: 0.4,
                '& .MuiButton-icon': {
                  opacity: 0.4,
                  '& svg': { color: palette.gray[800] },
                },
                '& > svg': { color: palette.gray[800], opacity: 0.4 },
              },
            },
            contained: {
              borderStyles: { border: `2px solid ${palette.blue[300]}` },
              backgroundColor: palette.blue[100],
              hoverBackground: palette.blue[200],
              hoverBoxShadow: 'unset',
              activeBackground: palette.blue[100],
              touchRippleBackground: palette.blue[500],
              touchRippleColor: palette.white,
              disabledStyles: {
                color: palette.gray[800],
                borderColor: palette.gray[500],
                '& .MuiButton-icon': { opacity: 0.4 },
              },
            },
          }),
        },
        '.mui-dialog-root &.MuiButtonGroup-grouped, .mui-modal-root &.MuiButtonGroup-grouped':
          createButtonGroupStyles({
            rootColor: palette.blue[600],
            outlined: {
              borderStyles: { border: `2px solid ${palette.blue[600]}` },
              color: palette.blue[600],
              hoverColor: palette.blue[700],
              activeColor: palette.blue[800],
              hoverOverrides: {
                border: `2px solid ${palette.blue[700]}`,
              },
              activeOverrides: {
                border: `2px solid ${palette.blue[800]}`,
              },
              touchRippleBackground: palette.blue[500],
              touchRippleColor: palette.blue[700],
              disabledStyles: {
                border: `2px solid ${palette.gray[600]}`,
                backgroundColor: palette.gray[50],
                color: palette.gray[600],
                '& .MuiButton-icon': { opacity: 0.4 },
              },
            },
            contained: {
              backgroundColor: palette.blue[600],
              color: palette.white,
              hoverBackground: palette.blue[700],
              activeBackground: palette.blue[800],
              touchRippleBackground: hexToRgba(palette.white, 0.4),
              touchRippleColor: palette.white,
              disabledStyles: {
                border: 'none',
                color: palette.gray[600],
                backgroundColor: palette.gray[200],
                '& .MuiButton-icon': { opacity: 0.4 },
              },
            },
          }),
      },
      defaultProps: {
        size: 'medium',
      },
    },
  },
  MuiRadio: {
    styleOverrides: {
      root: {
        padding: '0.5rem',
        '&.Mui-checked': {
          '& span svg': {
            fill: palette.blue[500],
          },
        },
        '&.Mui-disabled': {
          '& span svg': {
            fill: palette.gray[300],
          },
        },
      },
    },
    defaultProps: {
      size: 'medium',
      disableRipple: true,
    },
  },
  MuiCheckbox: {
    styleOverrides: {
      root: {
        color: palette.gray[900],
        '&.Mui-checked': {
          color: palette.blue[600],
        },
        '&.Mui-disabled': {
          color: palette.gray[400],
        },
        '& .MuiSvgIcon-root': { width: '1.25rem', height: '1.25rem' },
      },
    },
  },
  MuiInput: {
    styleOverrides: {
      root: {
        borderRadius: '0.375rem',
        '&.Mui-error': {
          borderColor: palette.red[600],
        },
        '&:focus': {
          borderColor: palette.blue[600],
        },
        '&.Mui-disabled': {
          color: palette.red[600],
        },
      },
    },
  },
  MuiAutocomplete: {
    styleOverrides: {
      root: {
        '& > .MuiFormControl-root': {
          width: '100%',
        },
      },
    },
  },
  MuiTextField: {
    defaultProps: {
      shrink: true,
    },
    styleOverrides: {
      root: {
        background: palette.white,
        width: '100%',
        borderRadius: '0.375rem',
        '&.Mui-error': {
          borderColor: palette.red[500],
        },
        '&:hover': {
          borderColor: palette.blue[600],
        },
        '&:focus': {
          borderColor: palette.blue[600],
        },
        variants: [
          {
            props: { multiline: true },
            style: {
              width: '100%',
            },
          },
        ],
        '& .MuiInputAdornment-root.MuiInputAdornment-positionEnd.MuiInputAdornment-outlined.MuiInputAdornment-sizeMedium .MuiButtonBase-root':
          {
            marginLeft: 'unset',
          },
        '&.MuiFormControl-root.MuiTextField-root': {
          paddingTop: 'unset',
        },
        '& .MuiInputLabel-root': {
          marginBottom: '0px',
          lineHeight: '1.5',
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: palette.gray[300], // default
        },
        '& .MuiOutlinedInput-root': {
          overflow: 'hidden',
          width: '100%',
        },
        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: palette.gray[300], // hover
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
          {
            borderColor: palette.blue[600], // focus
          },
        '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
          borderColor: palette.red[500], // error
        },
        '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline':
          {
            borderColor: palette.gray[300], // disabled
          },
      },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        borderRadius: '0.5rem',
      },
    },
  },
  MuiTextarea: {
    styleOverrides: {
      root: {
        borderColor: palette.gray[300],
      },
    },
  },
  MuiGrid: {
    styleOverrides: {
      root: {
        padding: 'unset',
      },
    },
  },
  MuiContainer: {
    defaultProps: {
      maxWidth: false,
      disableGutters: true,
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'capitalize',
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      filledSuccess: {
        backgroundColor: palette.green[100],
        color: palette.black,
      },
      sizeMedium: {
        fontSize: '1rem',
      },
      sizeSmall: {
        height: '2rem',
        padding: '0 0.5rem',
        paddingLeft: '0.35rem',
        paddingRight: '0.35rem',
        '& .MuiChip-deleteIcon': {
          width: '1.25rem',
          height: '1.25rem',
        },
        fontWeight: 500,
        gap: 2,
      },
      outlinedInfo: {
        borderColor: palette.blue[600],
        color: palette.gray[900],
      },
    },
  },
  MuiButtonBase: {
    defaultProps: {
      disableRipple: false,
    },
  },
  MuiMenu: {
    styleOverrides: {
      paper: {
        borderRadius: pixelsToRem(6),
        borderWidth: pixelsToRem(1),
        borderColor: 'gray.200',
      },
    },
  },
  MuiLink: {
    styleOverrides: {
      root: {
        textDecoration: 'none',
        lineHeight: 'auto',
        cursor: 'pointer',
        '&:hover': {
          textDecoration: 'underline',
        },
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        paddingTop: 0,
        variants: [
          {
            props: {
              size: 'md',
            },
            style: {
              width: '28rem',
            },
          },
          {
            props: {
              size: 'lg',
            },
            style: {
              width: '42rem',
            },
          },
          {
            props: {
              size: 'xl',
            },
            style: {
              width: '46rem',
            },
          },
        ],
      },
    },
  },
  MuiAccordion: {
    styleOverrides: {
      root: {
        borderWidth: pixelsToRem(1),
        borderRadius: pixelsToRem(4),
        overflowAnchor: 'none',
        marginBottom: '1rem',
        '&:before': {
          display: 'none',
        },
      },
    },
  },
  MuiAccordionDetails: {
    styleOverrides: {
      root: {
        paddingLeft: 4,
        paddingRight: 4,
        paddingBottom: 0,
      },
    },
  },
  MuiAccordionSummary: {
    styleOverrides: {
      root: {
        backgroundColor: '#F7FAFC',
        width: '100%',
        paddingLeft: pixelsToRem(16),
        '& > .MuiAccordionSummary-expandIconWrapper': {
          color: 'black',
        },
      },
    },
  },
  MuiInputBase: {
    styleOverrides: {
      root: {
        '& .MuiInputBase-input.MuiOutlinedInput-input': {
          padding: '0.5rem 0.625rem',
          maxHeight: '2.375rem',
        },
        '& input::placeholder, & textarea::placeholder': {
          color: palette.gray[500],
          opacity: 1,
        },
        '& .MuiOutlinedInput-input-root.Mui-disabled': {
          color: palette.gray[600],
          backgroundColor: palette.gray[100],
          '-webkit-text-fill-color': palette.gray[600],
        },
        '&.MuiInputBase-adornedStart': {
          paddingLeft: '0px',
        },
        '&.MuiInputBase-adornedEnd': {
          paddingRight: '0px',
        },
        '&.MuiButtonBase-root': {
          marginLeft: 'unset',
        },
        '&.Mui-error': {
          borderColor: palette.red[500],
        },
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: '6px',
        '&:not(.MuiInputBase-multiline)': {
          minHeight: '2.5rem',
        },
        overflow: 'hidden',
        '&.Mui-error': {
          boxShadow: `0 0 0 1px ${palette.red[600]}`,
          '&.Mui-focused': {
            boxShadow: 'none',
          },
        },
        '&.Mui-disabled > *': {
          borderWidth: 0,
        },
        borderColor: palette.gray[200],
        borderWidth: 1,
        borderStyle: 'solid',
        '&.Mui-disabled': {
          background: palette.gray[100],
          '.MuiInputAdornment-root::before': {
            background: palette.gray[100],
          },
        },
        '&.MuiInputBase-root.MuiOutlinedInput-root.Mui-disabled': {
          backgroundColor: palette.gray[100],
        },
      },
      error: {
        borderColor: palette.red[500],
      },
      focused: {
        borderColor: palette?.primary?.main,
      },
      disabled: {
        background: palette.gray[100],
      },
      input: {
        ':disabled': {
          color: palette.gray[600],
          '-webkit-text-fill-color': palette.gray[600],
        },
      },
      notchedOutline: {
        borderColor: palette.gray[200],
        borderWidth: '0 !important',
      },
    },
  },
  MuiInputAdornment: {
    styleOverrides: {
      positionStart: {
        ':before': {
          content: '""',
          position: 'absolute',
          left: 0,
          bottom: 0,
          top: 0,
          width: 38,
          borderTopLeftRadius: pixelsToRem(4),
          borderBottomLeftRadius: pixelsToRem(4),
          background: palette.gray[100],
          zIndex: -1,
        },
        '.date-picker &:before': {
          background: 'transparent',
        },
      },
      positionEnd: {
        ':before': {
          content: '""',
          position: 'absolute',
          right: 0,
          bottom: 0,
          top: 0,
          width: 38,
          borderTopRightRadius: pixelsToRem(4),
          borderBottomRightRadius: pixelsToRem(4),
          background: palette.gray[100],
          zIndex: -1,
        },
        '.date-picker &:before': {
          background: 'transparent',
        },
        '& .MuiButtonBase-root': {
          position: 'absolute',
          right: 0,
          width: 38,
          padding: 0,
          margin: 0,
        },
      },
      root: {
        zIndex: 1,
        color: palette.gray[600],
        fontWeight: 'bold',
        marginLeft: 'unset',
        width: 39,
        height: 38,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
    },
  },
  MuiSelect: {
    styleOverrides: {
      root: {
        borderRadius: '6px',
        '&.Mui-error': {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: `${palette.red[500]} !important`,
            borderWidth: '1px !important',
          },
          '& .MuiSvgIcon-root': {
            color: palette.red[500],
          },
        },
        '&.Mui-error:hover': {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: `${palette.red[700]} !important`,
          },
        },
        '&.Mui-error.Mui-focused': {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: `${palette.red[500]} !important`,
          },
        },
      },
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: {
        marginLeft: '0.625rem',
        marginTop: '0.125rem',
        fontSize: '0.75rem',
        lineHeight: '1rem',
        '&.Mui-error': {
          color: palette.error?.main,
        },
      },
    },
  },
  MuiModal: {
    styleOverrides: {
      root: {
        '&& .mui-modal-root': {
          borderRadius: '1rem',
        },
        '&& .mui-modal-root .mui-modal-content': {
          padding: '1.5rem',
        },
        '&& .mui-modal-root .mui-modal-content .mui-modal-header': {
          padding: 'unset',
        },
        '&& .mui-modal-root .mui-modal-content .mui-modal-body': {
          padding: '1rem 0 0 0',
          margin: 'unset',
        },
        '&& .mui-modal-root .mui-modal-content .mui-modal-footer': {
          padding: 'unset',
          marginTop: '1.5rem',
        },
      },
    },
  },
} as Components;

export default components;
